import { createContext, ReactNode, useContext } from 'react';
import { useGetReferenceData } from '../api/get-reference-data';
import type { GetReferenceDataResponse } from '../api/api.schemas';
import { formatState } from '@/util/helpers';

interface ReferenceDataContextType {
  data: GetReferenceDataResponse;
  getStateName: (abbreviation?: string | null) => string | null;
}

const ReferenceDataContext = createContext<ReferenceDataContextType | null>(null);

interface ReferenceDataProviderProps {
  children: ReactNode;
}

export function ReferenceDataProvider({ children }: ReferenceDataProviderProps) {
  const { data, isLoading } = useGetReferenceData();

  if (isLoading || !data) {
    return null;
  }

  const getStateName = (abbreviation?: string | null) => {
    if (!abbreviation) return '';
    const state = data.states.find((state) => state.abbreviation === abbreviation);
    return state ? formatState(state) : abbreviation;
  };

  return (
    <ReferenceDataContext.Provider value={{ data, getStateName }}>
      {children}
    </ReferenceDataContext.Provider>
  );
}

export function useReferenceData() {
  const contextData = useContext(ReferenceDataContext);

  if (!contextData) {
    throw new Error('useReferenceData must be used within a ReferenceDataProvider');
  }

  return contextData;
}
